import { fetchCustomerCatalog } from '../utils/customerRegistryRequests';
import { useCustomerRegistryRefresh } from '../utils/customerRegistryEvents';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaFileInvoiceDollar, FaPlus, FaSyncAlt, FaTrashAlt, FaRegFileAlt, FaCog, FaTasks, FaSearch, FaThLarge } from 'react-icons/fa';
import './NfseManager.css';
import api from '../api';
import InfoTip from '../components/InfoTip';
import NfseServicePicker from '../components/NfseServicePicker';
import { fiscalName } from '../utils/fiscalName';
import { renderNfseCustomerDescription } from '../utils/nfseDescriptionPresets';

const localDate = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const emptyTemplate = () => ({ name: '', serviceCode: '', serviceDescription: '', defaultAmount: '' });
const emptyEmission = () => ({
  series: '1', number: '', customerMunicipality: '', customerCep: '',
  customerStreet: '', customerNumber: '', customerDistrict: '',
  placeOfService: '', issTaxation: '1', issWithholding: '1',
  simplesRate: '',
});
const EMISSION_FIELDS = [
  ['Série', 'series', '1 a 5 dígitos'],
  ['Número da DPS', 'number', 'Número ainda não utilizado'],
  ['Município do cliente (IBGE)', 'customerMunicipality', '7 dígitos'],
  ['CEP', 'customerCep', '8 dígitos'],
  ['Rua / avenida', 'customerStreet', 'Endereço do cliente'],
  ['Número', 'customerNumber', 'Número ou S/N'],
  ['Bairro', 'customerDistrict', 'Bairro'],
  ['Município do serviço (IBGE)', 'placeOfService', '7 dígitos'],
];
const emptyForm = () => ({
  customerTaxId: '', customerName: '', customerEmail: '',
  serviceCode: '', serviceDescription: '', serviceAmount: '',
  competenceDate: localDate(),
});

const money = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
const formatDocument = value => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 14);
  if (digits.length > 11) return digits.replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2');
  return digits.replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
};
const dateLabel = (value) => value ? new Date(String(value).slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—';
const requestError = (error) => error?.response?.data?.error || 'Não foi possível concluir a operação.';
const nfseRejectionReason = (note) => {
  const reason = String(note?.lastError || '').trim();
  if (!reason || /^rejei[cç][aã]o$/i.test(reason)) {
    return 'O motivo detalhado da SEFIN não foi preservado nesta tentativa anterior. Não é possível afirmar qual regra fiscal foi rejeitada.';
  }
  return reason;
};


const NAV_ITEMS = [
  { id: 'overview', label: 'Visão geral', Icon: FaThLarge },
  { id: 'drafts', label: 'Documentos', Icon: FaRegFileAlt },
  { id: 'templates', label: 'Modelos', Icon: FaFileInvoiceDollar },
  { id: 'automations', label: 'Automações', Icon: FaTasks },
  { id: 'consultations', label: 'Consultas', Icon: FaSearch },
  { id: 'settings', label: 'Configurações', Icon: FaCog },
];
const statusLabel = (status) => ({
  DRAFT: 'Rascunho', PROCESSING: 'Conciliação necessária', AUTHORIZED: 'Autorizada',
  TEST_AUTHORIZED: 'Teste autorizado (sem valor fiscal)',
  CANCELED: 'Cancelada', REJECTED: 'Rejeitada',
}[status] || status || 'Desconhecido');

export default function NfseManager({ openTab, configurationOnly = false, initialView = 'settings' }) {
  const [activeView, setActiveView] = useState(configurationOnly ? initialView : 'overview');
  const openRegistration = async (initialPanel = 'fiscal') => {
    if (!openTab) return;
    const { default: CompanyProfile } = await import('./CompanyProfile');
    openTab('company', 'Dados Cadastrais', CompanyProfile, { openTab, initialPanel, initialFiscalContext: emissionNote ? { competence: String(emissionNote.competenceDate).slice(0, 7), serviceCode: emissionNote.serviceCode } : undefined });
  };
  const draftBaseline = useRef(null);
  const saveLock = useRef(false);

  const [company, setCompany] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerMatches, setCustomerMatches] = useState([]);
  const [selectedCustomerCache, setSelectedCustomerCache] = useState(null);
  const [customerSearchBusy, setCustomerSearchBusy] = useState(false);
  const [draftTemplateId, setDraftTemplateId] = useState('');
  const [selectedDescriptionName, setSelectedDescriptionName] = useState('');
  const [documents, setDocuments] = useState([]);
  const [historyKey, setHistoryKey] = useState('');
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyFeedback, setHistoryFeedback] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [scheduleRule, setScheduleRule] = useState({ enabled: false, mapping: {}, templateId: null });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateForm, setTemplateForm] = useState(emptyTemplate);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [feedback, setFeedback] = useState('');
  const [integration, setIntegration] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificatePassword, setCertificatePassword] = useState('');
  const [certificateLabel, setCertificateLabel] = useState('');
  const [certificateSaving, setCertificateSaving] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const preflightRef = useRef(null);
  const [preflightNeedsRefresh, setPreflightNeedsRefresh] = useState(false);
  const autoPreflightAttempted = useRef(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceMatches, setServiceMatches] = useState([]);
  const [serviceLookupBusy, setServiceLookupBusy] = useState(false);
  const [serviceLookupError, setServiceLookupError] = useState('');
  const [serviceSaving, setServiceSaving] = useState(false);

  const [accessKey, setAccessKey] = useState('');
  const [consulting, setConsulting] = useState(false);
  const [officialResult, setOfficialResult] = useState(null);
  const [dpsSeries, setDpsSeries] = useState('');
  const [dpsNumber, setDpsNumber] = useState('');
  const [dpsBusy, setDpsBusy] = useState(false);
  const [dpsResult, setDpsResult] = useState(null);
  const [processId, setProcessId] = useState('');
  const [processInfo, setProcessInfo] = useState(null);
  const [processMapping, setProcessMapping] = useState({});
  const [processTemplateId, setProcessTemplateId] = useState('');
  const [processBusy, setProcessBusy] = useState(false);
  const [error, setError] = useState('');
  const [emissionNote, setEmissionNote] = useState(null);
  const [emissionForm, setEmissionForm] = useState(emptyEmission);
  const [emissionPreview, setEmissionPreview] = useState(null);
  const [emissionBusy, setEmissionBusy] = useState(false);
  const [emissionDetails, setEmissionDetails] = useState(false);
  const [emissionDefaultsBusy, setEmissionDefaultsBusy] = useState(false);
  const [sequenceBusy, setSequenceBusy] = useState(false);
  const sequenceLock = useRef(false);
  const [emissionPreviewBusy, setEmissionPreviewBusy] = useState(false);
  const [emissionMissing, setEmissionMissing] = useState([]);
  const emissionSendLock = useRef(false);
  const [postalBusy, setPostalBusy] = useState(false);
  const [postalNotice, setPostalNotice] = useState('');
  const postalAutofill = useRef({});
  const emissionPrefillRequest = useRef(0);
  const emissionManualEdit = useRef(false);
  const [emissionPrefillNotice, setEmissionPrefillNotice] = useState('');

  const syncLock = useRef(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [syncedAt, setSyncedAt] = useState('');
  const [syncPending, setSyncPending] = useState(0);
  const [syncPartial, setSyncPartial] = useState(false);
  const [cancelNote, setCancelNote] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelCode, setCancelCode] = useState('1');
  const [cancelBusy, setCancelBusy] = useState(false);
  const cancelLock = useRef(false);
  const [serviceOverrideBusy, setServiceOverrideBusy] = useState(false);
  const rateEdited = useRef(false);
  const syncDocuments = useCallback(async () => {
    if (syncLock.current) return;
    syncLock.current = true; setSyncBusy(true); setSyncError('');
    try {
      const response = await api.post('/nfse/documents/sync', {});
      if (Array.isArray(response.data?.documents)) setDocuments(response.data.documents);
      else { const list = await api.get('/nfse'); setDocuments(Array.isArray(list.data) ? list.data : []); }
      setSyncedAt(response.data?.syncedAt || new Date().toISOString());
      setSyncPartial(Boolean(response.data?.partial));
      setSyncPending(Number(response.data?.pendingCount) || 0);
      setSyncError([...(response.data?.errors || []).map(item => `Nota ${item.id}: ${item.error}`),
        response.data?.history?.error ? `Histórico: ${response.data.history.error}` : ''].filter(Boolean).join(' · '));
    } catch (failure) { setSyncError(requestError(failure)); }
    finally { syncLock.current = false; setSyncBusy(false); }
  }, []);
  const customerRefreshSequence = useRef(0);
  useCustomerRegistryRefresh(async kind => {
    if (kind === 'templates') return;
    const sequence = ++customerRefreshSequence.current;
    try {
      const records = await fetchCustomerCatalog(api, {}, () => sequence === customerRefreshSequence.current);
      if (records) {setCustomers(records);setCustomerMatches([]);}
    } catch { /* Keep existing options and the draft on a failed refresh. */ }
  });
  const loadLock = useRef(false);
  const load = useCallback(async () => {
    if (loadLock.current) return;
    loadLock.current = true;
    setLoading(true);
    setError('');
    const tasks = [
      { label: 'empresa', path: '/company', apply: data => setCompany(data || null) },
      { label: 'documentos', path: '/nfse', apply: data => setDocuments(Array.isArray(data) ? data : []) },
      { label: 'clientes', request: () => fetchCustomerCatalog(api), apply: data => setCustomers(Array.isArray(data) ? data : []) },
      { label: 'integração', path: '/nfse/integration/status', apply: data => setIntegration(data || null) },
      { label: 'modelos', path: '/nfse/templates', apply: data => setTemplates(Array.isArray(data) ? data : []) },
      { label: 'pendências cadastrais', path: '/nfse/readiness', apply: data => setReadiness(data || null) },
      { label: 'agendamentos', path: '/nfse/schedules', apply: data => setSchedules(Array.isArray(data) ? data : []) },
      { label: 'certificado', path: '/nfse/integration/certificate', apply: data => setCertificate(data || null) },
      { label: 'pré-homologação', path: '/nfse/integration/preflight', apply: data => {
          setPreflight(data?.report || null);
          setPreflightNeedsRefresh(Boolean(data?.needsRefresh));
        } },
    ];
    try {
      const results = await Promise.allSettled(tasks.map(task => task.request ? task.request().then(data=>({data})) : api.get(task.path)));
      const failed = [];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          tasks[index].apply(result.value.data);
        } else {
          failed.push(tasks[index].label);
        }
      });
      if (failed.length) {
        setError('Algumas informações não puderam ser carregadas (' + failed.join(', ') + '). As outras áreas continuam disponíveis. Tente atualizar.');
      }
    } catch (requestFailure) {
      setError(requestError(requestFailure));
    } finally {
      setLoading(false);
      await syncDocuments();
      loadLock.current = false;
    }
  }, [syncDocuments]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (configurationOnly) return undefined;
    const check = () => { if (document.visibilityState === 'visible') syncDocuments(); };
    const timer = setInterval(check, 30000);
    document.addEventListener('visibilitychange', check);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', check); };
  }, [syncDocuments, configurationOnly]);
  const confirmCancellation = async event => {
    event.preventDefault();
    if (!cancelNote || cancelLock.current || cancelReason.trim().length < 15 || cancelReason.trim().length > 255) return;
    cancelLock.current = true; setCancelBusy(true); setError(''); setFeedback('');
    try {
      const response = await api.post(`/nfse/documents/${cancelNote.id}/cancel`, { reasonCode: cancelCode, reason: cancelReason.trim(), confirm: 'CANCELAR_NFSE' });
      if (response.data?.id) setDocuments(previous => previous.map(note => note.id === response.data.id ? response.data : note));
      setFeedback(response.data?.status === 'CANCELED' ? 'Cancelamento confirmado pelo governo.' : response.data?.warning || 'Cancelamento ainda não confirmado. Aguarde a consulta oficial; não reenvie o pedido.');
      setCancelNote(null);
      await syncDocuments();
    } catch (failure) {
      setError(requestError(failure) + ' O cancelamento não foi confirmado. Confira a situação oficial antes de tentar novamente.');
      setCancelNote(null);
      await syncDocuments();
    } finally { cancelLock.current = false; setCancelBusy(false); }
  };
  const changeEmissionService = async serviceCode => {
    if (!emissionNote || serviceOverrideBusy || serviceCode === emissionNote.serviceCode) return;
    const requestId = emissionPrefillRequest.current;
    setServiceOverrideBusy(true); setEmissionPreview(null); setError('');
    try {
      const payload = {};
      ['customerId', 'customerTaxId', 'customerName', 'customerEmail', 'serviceDescription', 'serviceAmount', 'competenceDate'].forEach(key => { payload[key] = emissionNote[key]; });
      const response = await api.put(`/nfse/${emissionNote.id}`, { ...payload, serviceCode });
      if (requestId === emissionPrefillRequest.current) setEmissionNote({ ...emissionNote, ...response.data, serviceCode });
    } catch (failure) { setError(requestError(failure)); }
    finally { setServiceOverrideBusy(false); }
  };
  const draftSnapshot = () => JSON.stringify({
    form, selectedCustomerId, draftTemplateId, selectedDescriptionName,
  });
  const draftIsDirty = () => showForm && draftBaseline.current !== null
    && draftBaseline.current !== draftSnapshot();
  const closeDraft = () => {
    if (draftIsDirty() && !window.confirm('Existem alterações não salvas neste rascunho. Deseja descartá-las?')) return;
    draftBaseline.current = null;
    setShowForm(false);
    setEditingDraftId(null);
  };
  const switchView = (view) => {
    if (!configurationOnly && ['settings', 'templates', 'automations'].includes(view)) { openRegistration(view === 'settings' ? 'certificate' : view); return; }
    if (view === activeView) return;
    if (showForm && draftIsDirty() && !window.confirm('Há um rascunho não salvo. Deseja sair sem salvar?')) return;
    draftBaseline.current = null;
    setShowForm(false);
    setActiveView(view);
  };
  useEffect(() => {
    if (!showForm) return undefined;
    const handler = event => {
      if (draftBaseline.current === null || draftBaseline.current === JSON.stringify({
        form, selectedCustomerId, draftTemplateId, selectedDescriptionName,
      })) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [showForm, form, selectedCustomerId, draftTemplateId, selectedDescriptionName]);

  // Pesquisa remota para que tomadores além dos primeiros 300 cadastros
  // também estejam disponíveis após importações em lote.
  useEffect(() => {
    const query = customerSearch.trim();
    if (!showForm || activeView !== 'drafts' || !query) {
      setCustomerMatches([]);
      setCustomerSearchBusy(false);
      return undefined;
    }
    let active = true;
    const timer = setTimeout(async () => {
      setCustomerSearchBusy(true);
      try {
        const response = await api.get('/customers', { params: { q: query, limit: 60 } });
        if (active) setCustomerMatches(Array.isArray(response.data) ? response.data : []);
      } catch {
        if (active) setCustomerMatches([]);
      } finally {
        if (active) setCustomerSearchBusy(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [customerSearch, showForm, activeView]);

  const filtered = useMemo(() => documents.filter((document) => {
    const text = fiscalName([document.customerName, document.customerTaxId, document.serviceDescription, document.accessKey, document.invoiceNumber, document.dpsNumber, document.dpsSeries].join(' '));
    return text.includes(fiscalName(query)) && (statusFilter === 'all' || (statusFilter === 'EXTERNAL' ? document.origin === 'EXTERNAL' : document.status === statusFilter));
  }), [documents, query, statusFilter]);
  const issued = documents.filter(note => note.status === 'AUTHORIZED');
  const pending = documents.filter(note => note.status === 'DRAFT');

  const openNew = () => {
    if (showForm && draftIsDirty() && !window.confirm('Descartar o rascunho em edição e iniciar outro?')) return;
    setFeedback('');
    setError('');
    setEmissionNote(null);
    ++emissionPrefillRequest.current;
    setEditingDraftId(null);
    setSelectedCustomerId('');
    setCustomerSearch('');
    setDraftTemplateId('');
    setSelectedDescriptionName('');
    setActiveView('drafts');
    const nextForm = {
      ...emptyForm(),
      serviceCode: company?.defaultServiceCode || '',
      serviceDescription: company?.defaultServiceDescription || '',
    };
    setForm(nextForm);
    draftBaseline.current = JSON.stringify({ form: nextForm, selectedCustomerId: '', draftTemplateId: '', selectedDescriptionName: '' });
    setShowForm(true);
  };

  const saveDraft = async (event) => {
    event.preventDefault();
    const saveOnly = event.nativeEvent?.submitter?.value === 'draft';
    if (saveLock.current) return;
    saveLock.current = true;
    setSaving(true);
    setError('');
    setFeedback('');
    try {
      const payload = { ...form, customerId: selectedCustomerId ? Number(selectedCustomerId) : null };
      // Consultar o banco (e não só os 200 registros carregados) antes de salvar.
      // A decisão final continua humana: a igualdade de campos não prova duplicidade fiscal.
      if (!editingDraftId) {
        const duplicates = await api.post('/nfse/drafts/potential-duplicates', {
          customerTaxId: form.customerTaxId, competenceDate: form.competenceDate,
          serviceCode: form.serviceCode, serviceAmount: form.serviceAmount,
        });
        const records = duplicates.data?.possible || [];
        if (records.length && !window.confirm(
          'Já existe' + (records.length === 1 ? '' : 'm') + ' ' + records.length +
          ' documento(s) com este tomador, competência, serviço e valor (IDs: ' +
          records.map(item => '#' + item.id + ' ' + statusLabel(item.status)).join(', ') +
          '). Verifique antes de continuar. Deseja salvar um novo rascunho mesmo assim?'
        )) return;
      }
      const result = editingDraftId
        ? await api.put(`/nfse/${editingDraftId}`, payload)
        : await api.post('/nfse', payload);
      draftBaseline.current = null;
      setShowForm(false);
      setEditingDraftId(null);
      setFeedback('Rascunho salvo. Ele ainda não foi transmitido ao governo.');
      await load();
      if (!saveOnly) await openEmission(result.data);
    } catch (requestFailure) {
      setError(requestError(requestFailure));
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  };

  const editDraft = (note) => {
    if (showForm && draftIsDirty() && !window.confirm('Descartar alterações atuais antes de editar outro rascunho?')) return;
    setEmissionNote(null);
    ++emissionPrefillRequest.current;
    setActiveView('drafts');
    setSelectedCustomerId(note.customerId ? String(note.customerId) : '');
    setCustomerSearch('');
    setDraftTemplateId('');
    setSelectedDescriptionName('');
    setEditingDraftId(note.id);
    const nextForm = {
      customerTaxId: note.customerTaxId || '',
      customerName: note.customerName || '',
      customerEmail: note.customerEmail || '',
      serviceCode: note.serviceCode || '',
      serviceDescription: note.serviceDescription || '',
      serviceAmount: note.serviceAmount || '',
      competenceDate: String(note.competenceDate || localDate()).slice(0, 10),
    };
    draftBaseline.current = JSON.stringify({ form: nextForm,
      selectedCustomerId: note.customerId ? String(note.customerId) : '',
      draftTemplateId: '', selectedDescriptionName: '' });
    setForm(nextForm);
    setShowForm(true);
    setError('');
  };

  const reuseDocument = (note) => {
    if (showForm && draftIsDirty() && !window.confirm('Descartar alterações atuais e reutilizar este documento?')) return;
    setActiveView('drafts');
    setShowForm(true);
    setEditingDraftId(null);
    setSelectedCustomerId(note.customerId ? String(note.customerId) : '');
    setCustomerSearch('');
    setDraftTemplateId('');
    setSelectedDescriptionName('');
    setError('');
    setFeedback('Dados reaproveitados. Revise competência e valor antes de salvar.');
    const nextForm = {
      customerTaxId: note.customerTaxId || '', customerName: note.customerName || '',
      customerEmail: note.customerEmail || '', serviceCode: note.serviceCode || '',
      serviceDescription: note.serviceDescription || '',
      serviceAmount: note.serviceAmount || '', competenceDate: localDate(),
    };
    draftBaseline.current = JSON.stringify({ form: nextForm,
      selectedCustomerId: note.customerId ? String(note.customerId) : '',
      draftTemplateId: '', selectedDescriptionName: '' });
    setForm(nextForm);
  };

  const saveTemplate = async (event) => {
    event.preventDefault();
    setTemplateBusy(true);
    setError('');
    try {
      if (editingTemplateId) {
        await api.put(`/nfse/templates/${editingTemplateId}`, templateForm);
      } else {
        await api.post('/nfse/templates', templateForm);
      }
      setEditingTemplateId(null);
      setTemplateForm(emptyTemplate());
      setFeedback('Modelo de serviço salvo.');
      await load();
    } catch (failure) {
      setError(requestError(failure));
    } finally {
      setTemplateBusy(false);
    }
  };
  const deleteTemplate = async (template) => {
    if (!window.confirm('Excluir o modelo ' + template.name + '?')) return;
    setTemplateBusy(true);
    setError('');
    try {
      await api.delete(`/nfse/templates/${template.id}`);
      if (editingTemplateId === template.id) { setEditingTemplateId(null); setTemplateForm(emptyTemplate()); }
      setFeedback('Modelo excluído.');
      await load();
    } catch (failure) {
      setError(requestError(failure));
    } finally {
      setTemplateBusy(false);
    }
  };

  const removeDraft = async (id) => {
    if (!window.confirm('Excluir este rascunho? Nenhum documento fiscal será cancelado.')) return;
    setError('');
    try {
      await api.delete(`/nfse/${id}`);
      setFeedback('Rascunho excluído.');
      await load();
    } catch (requestFailure) {
      setError(requestError(requestFailure));
    }
  };

  const consultOfficial = async (kind) => {
    setConsulting(true);
    setError('');
    setOfficialResult(null);
    try {
      const key = accessKey.replace(/\D/g, '');
      if (key.length !== 50) throw new Error('Informe a chave de acesso da NFS-e com 50 dígitos.');
      const suffix = kind === 'events' ? '/events' : '';
      const response = await api.get(`/nfse/official/${key}${suffix}`);
      setOfficialResult({ type: kind === 'events' ? 'Eventos fiscais' : 'Consulta NFS-e', data: response.data });
    } catch (requestFailure) {
      setError(requestFailure?.response ? requestError(requestFailure) : requestFailure.message);
    } finally {
      setConsulting(false);
    }
  };

  const selectedSchedule = schedules.find(item => String(item.id) === selectedScheduleId);
  const selectSchedule = (id) => {
    setSelectedScheduleId(id);
    const found = schedules.find(item => String(item.id) === id);
    setScheduleRule(found?.nfseDraft
      ? { enabled: Boolean(found.nfseDraft.enabled), mapping: { ...(found.nfseDraft.mapping || {}) }, templateId: found.nfseDraft.templateId || null }
      : { enabled: false, mapping: {}, templateId: null });
  };
  const saveSchedule = async () => {
    if (!selectedScheduleId) return;
    setSavingSchedule(true);
    setError('');
    try {
      await api.put(`/nfse/schedules/${selectedScheduleId}/draft-config`, scheduleRule);
      setFeedback('Configuração salva. Os rascunhos serão gerados somente quando o formulário contiver os dados necessários. Não há emissão fiscal.');
      await load();
    } catch (failure) {
      setError(requestError(failure));
    } finally {
      setSavingSchedule(false);
    }
  };

  const openEmission = async (note) => {
    if (showForm && draftIsDirty() && !window.confirm('Descartar edição pendente antes da conferência fiscal?')) return;
    setShowForm(false);
    setEmissionDetails(false);
    rateEdited.current = false;
    setEmissionDefaultsBusy(true);
    setEmissionMissing([]);
    setPostalNotice('');
    postalAutofill.current = {};
    const customer = customers.find(row => row.id === note.customerId);
    const requestId = ++emissionPrefillRequest.current;
    emissionManualEdit.current = false;
    setActiveView('drafts');
    setEmissionNote(note);
    setEmissionPreview(null);
    setEmissionPrefillNotice('Preenchendo campos a partir do cadastro do cliente...');
    setEmissionForm({
      ...emptyEmission(),
      customerCep: customer?.zipCode || '',
      placeOfService: company?.municipalityIbgeCode || '',
    });
    setError('');
    setFeedback('');
    try {
      const response = await api.get('/nfse/' + note.id + '/emission-defaults');
      if (requestId !== emissionPrefillRequest.current || emissionManualEdit.current) return;
      setEmissionForm(previous => ({
        ...previous, ...(response.data || {}),
      }));
      const values = { ...emptyEmission(), ...(response.data || {}) };
      setEmissionMissing(EMISSION_FIELDS.filter(([, key]) => !String(values[key] ?? '').trim()).map(([, key]) => key));
      setEmissionPrefillNotice([response.data?.warning, response.data?.source, 'Cadastro e sequência DPS preenchidos automaticamente. Confira o resumo antes de emitir.'].filter(Boolean).join(' · '));
    } catch {
      if (requestId === emissionPrefillRequest.current) {
        setEmissionDetails(true);
        setEmissionPrefillNotice('Não foi possível preencher o cadastro. Tente abrir a nota novamente ou complete os dados abaixo.');
      }
    } finally {
      if (requestId === emissionPrefillRequest.current) setEmissionDefaultsBusy(false);
    }
  };
  const refreshDpsSequence = async () => {
    if (!emissionNote || sequenceLock.current || emissionBusy) return;
    const requestId = emissionPrefillRequest.current;
    sequenceLock.current = true; setSequenceBusy(true); setEmissionPreview(null);
    try {
      const response = await api.get(`/nfse/${emissionNote.id}/emission-defaults`);
      if (requestId !== emissionPrefillRequest.current) return;
      const series = response.data?.series || '1';
      const number = response.data?.number || '';
      setEmissionForm(previous => ({ ...previous, series, number }));
      setEmissionMissing(previous => [...previous.filter(key => !['series', 'number'].includes(key)), ...(!number ? ['number'] : [])]);
      setEmissionPrefillNotice([response.data?.warning, response.data?.source, number ? `Sequência DPS atualizada: série ${series}, número ${number}.` : 'Sequência DPS ainda indisponível. Tente atualizar novamente.'].filter(Boolean).join(' · '));
    } catch (failure) {
      if (requestId === emissionPrefillRequest.current) setEmissionPrefillNotice('Não foi possível atualizar a sequência DPS. ' + requestError(failure) + ' Tente atualizar novamente.');
    } finally { sequenceLock.current = false; setSequenceBusy(false); }
  };
  const updateEmissionField = (field, value) => {
    emissionManualEdit.current = true;
    setEmissionPreview(null);
    setEmissionForm(previous => ({ ...previous, [field]: value }));
  };
  useEffect(() => {
    const cep = String(emissionForm.customerCep || '').replace(/\D/g, '');
    if (!emissionNote || emissionDefaultsBusy || cep.length !== 8) return undefined;
    let current = true;
    setPostalBusy(true);
    const timer = setTimeout(async () => {
      try {
        const response = await api.get('/nfse/postal-address/' + cep);
        if (!current) return;
        const address = response.data;
        if (!address?.municipality) return;
        const fields = { customerStreet: address.street, customerDistrict: address.district, customerMunicipality: address.municipality };
        setEmissionPreview(null);
        setEmissionForm(previous => {
          if (String(previous.customerCep).replace(/\D/g, '') !== cep) return previous;
          const next = { ...previous };
          Object.entries(fields).forEach(([key, value]) => {
            if (value && (!previous[key] || previous[key] === postalAutofill.current[key])) next[key] = value;
          });
          postalAutofill.current = fields;
          return next;
        });
        setPostalNotice('Endereço localizado pelo CEP. Número obtido do cadastro, quando disponível.');
      } catch {
        if (current) setPostalNotice('Não foi possível consultar o CEP agora. Os dados do cadastro foram preservados.');
      } finally { if (current) setPostalBusy(false); }
    }, 350);
    return () => { current = false; clearTimeout(timer); setPostalBusy(false); };
  }, [emissionNote, emissionDefaultsBusy, emissionForm.customerCep]);
  useEffect(() => {
    if (!emissionNote || sequenceBusy || serviceOverrideBusy || emissionDefaultsBusy || postalBusy || !String(emissionForm.simplesRate || '').trim() || EMISSION_FIELDS.some(([, key]) => !String(emissionForm[key] ?? '').trim())) return undefined;
    let current = true;
    setEmissionPreviewBusy(true);
    const timer = setTimeout(async () => {
      setError('');
      try {
        const response = await api.post('/nfse/' + emissionNote.id + '/emission-preview', emissionForm);
        if (current) { setEmissionPreview(response.data); setEmissionMissing([]); }
      } catch (failure) {
        if (current) setError(requestError(failure));
      } finally {
        if (current) setEmissionPreviewBusy(false);
      }
    }, 500);
    return () => { current = false; clearTimeout(timer); setEmissionPreviewBusy(false); };
  }, [emissionNote, emissionForm, emissionDefaultsBusy, postalBusy, serviceOverrideBusy, sequenceBusy]);
  useEffect(() => {
    const refreshSettings = async () => {
      await load();
      if (!emissionNote || emissionBusy || rateEdited.current) return;
      try {
        const response = await api.get('/nfse/' + emissionNote.id + '/emission-defaults');
        setEmissionPreview(null);
        setEmissionForm(previous => ({ ...previous, simplesRate: response.data?.simplesRate || '' }));
      } catch { /* Uma nova tentativa continua disponível ao reabrir a nota. */ }
    };
    window.addEventListener('nfse-settings-updated', refreshSettings);
    return () => window.removeEventListener('nfse-settings-updated', refreshSettings);
  }, [load, emissionNote, emissionBusy]);
  const sendEmission = async () => {
    if (!emissionNote || !emissionPreview || emissionBusy || emissionSendLock.current) return;
    const isLive = emissionPreview.environment === 'production';
    if (!window.confirm(isLive
      ? 'ATENÇÃO: PRODUÇÃO FISCAL REAL. Confirma emitir NFS-e de ' + money(emissionNote.serviceAmount) + ' para ' + fiscalName(emissionNote.customerName) + '? Se autorizada, a nota terá efeitos fiscais e não será um teste.'
      : 'Confirma transmissão à produção restrita? Esta nota é APENAS TESTE, sem validade fiscal.')) return;
    emissionSendLock.current = true;
    setEmissionBusy(true);
    setError('');
    try {
      const response = await api.post('/nfse/' + emissionNote.id + '/emitir', {
        ...emissionForm, previewHash: emissionPreview.previewHash, confirm: 'EMITIR_NFSE',
      });
      setEmissionNote(null);
      setEmissionPreview(null);
      if (response.data?.status === 'PROCESSING') {
        setFeedback('Resultado incerto. Não reenvie. Consulte a situação da DPS no portal e confira o status.');
      } else {
        setFeedback(response.data?.status === 'TEST_AUTHORIZED'
          ? 'DPS aceita no ambiente de testes. Documento sem validade fiscal.'
          : 'A SEFIN informou autorização. Consulte o XML oficial e a chave de acesso.');
      }
      await load();
    } catch (failure) {
      if (failure?.response?.status === 422) {
        setEmissionNote(null);
        setEmissionPreview(null);
        const reason = requestError(failure);
        const guidance = failure?.response?.data?.guidance;
        setFeedback('');
        await load();
        setError('NFS-e rejeitada pela SEFIN: ' + reason
          + (guidance ? ' Como corrigir: ' + guidance : '')
          + ' A nota rejeitada foi preservada. Use Reutilizar para criar um novo rascunho, sem reenvio automático.');
      } else {
        setError(requestError(failure));
      }
    } finally {
      emissionSendLock.current = false;
      setEmissionBusy(false);
    }
  };
  const reconcileEmission = async (note) => {
    if (!window.confirm('Consultar a DPS na SEFIN sem reenvio?')) return;
    setError('');
    setEmissionBusy(true);
    try {
      const response = await api.post('/nfse/' + note.id + '/reconcile-emission', {});
      setFeedback(response.data?.status === 'AUTHORIZED' || response.data?.status === 'TEST_AUTHORIZED'
        ? 'NFS-e localizada e XML recuperado na SEFIN.'
        : response.data?.note || 'DPS ainda pendente de conciliação.');
      await load();
    } catch (failure) { setError(requestError(failure)); }
    finally { setEmissionBusy(false); }
  };

  const importHistory = async (mode) => {
    if (historyBusy) return;
    setHistoryBusy(true); setHistoryFeedback(''); setError('');
    try {
      if (mode === 'key' && !/^\d{50}$/.test(historyKey)) throw new Error('Informe a chave de acesso com 50 dígitos.');
      const response = await api.post(mode === 'key' ? '/nfse/history/import-by-key' : '/nfse/history/import-adn',
        mode === 'key' ? { accessKey: historyKey } : {});
      const result = response.data || {};
      setHistoryFeedback(mode === 'key'
        ? (result.imported ? 'NFS-e anterior importada com sucesso.' : 'Essa NFS-e já está cadastrada.')
        : (result.imported || 0) + ' importadas · ' + (result.existing || 0) + ' existentes · ' + (result.skipped || 0) + ' ignoradas. ' + (result.note || ''));
      await load();
    } catch (failure) { setError(failure?.message === 'Informe a chave de acesso com 50 dígitos.' ? failure.message : requestError(failure)); }
    finally { setHistoryBusy(false); }
  };
  const downloadPdf = async (note) => {
    setError('');
    try {
      const response = await api.get('/nfse/' + note.id + '/danfse', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'DANFSe-' + note.accessKey + '.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (failure) { setError(requestError(failure)); }
  };

  const downloadXml = async (note) => {
    setError('');
    try {
      const response = await api.get('/nfse/' + note.id + '/xml', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/xml;charset=utf-8' });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = 'NFSe-' + note.accessKey + '.xml';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (failure) { setError(requestError(failure)); }
  };

  const saveCertificate = async (event) => {
    event.preventDefault();
    setError('');
    setFeedback('');
    if (!certificateFile) return setError('Selecione seu arquivo de certificado A1 (.pfx ou .p12).');
    if (certificateFile.size > 256 * 1024 || certificateFile.size < 128) {
      return setError('O arquivo do certificado deve ter entre 128 bytes e 256 KB.');
    }
    setCertificateSaving(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Não foi possível ler o certificado.'));
        reader.readAsDataURL(certificateFile);
      });
      const base64 = dataUrl.split(',')[1];
      if (!base64) throw new Error('Não foi possível processar o arquivo.');
      const savedCertificate = await api.put('/nfse/integration/certificate', {
        pfxBase64: base64,
        password: certificatePassword,
        label: certificateLabel || certificateFile.name,
      });
      setCertificatePassword('');
      setCertificateFile(null);
      setCertificateLabel('');
      window.dispatchEvent(new Event('nfse-settings-updated'));
      setPreflight(savedCertificate.data?.preflight || null);
      setFeedback(savedCertificate.data?.preflight
        ? 'Certificado A1 protegido. Pré-homologação automática executada; veja as correções necessárias abaixo.'
        : 'Certificado A1 cadastrado, mas a verificação automática não pôde ser concluída. Use Reexecutar verificações para obter o diagnóstico.');
      await load();
      preflightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (failure) {
      setError(failure?.response ? requestError(failure) : failure.message || 'Erro ao cadastrar certificado.');
    } finally {
      setCertificatePassword('');
      setCertificateSaving(false);
    }
  };
  const runPreflight = async () => {
    if (preflightBusy || certificateSaving) return;
    setPreflightBusy(true);
    setError('');
    setFeedback('');
    try {
      const response = await api.post('/nfse/integration/preflight', {});
      setPreflight(response.data?.report || null);
      setPreflightNeedsRefresh(false);
      try {
        const status = await api.get('/nfse/integration/status');
        setIntegration(status.data || null);
      } catch { /* O diagnóstico permanece visível se a consulta complementar falhar. */ }
      // A verificação de fundo não precisa interromper o fluxo com avisos de sucesso.
    } catch (failure) {
      setError(requestError(failure));
    } finally {
      setPreflightBusy(false);
    }
  };

  // Ao abrir Configurações, refaz automaticamente o diagnóstico apenas se
  // estiver ausente, desatualizado ou o cadastro fiscal tiver mudado.
  useEffect(() => {
    if (!loading && certificate?.usable && preflightNeedsRefresh && !autoPreflightAttempted.current) {
      autoPreflightAttempted.current = true;
      runPreflight();
    }
  }, [activeView, loading, certificate?.usable, preflightNeedsRefresh]);

  useEffect(() => {
    if (activeView !== 'settings' || serviceSearch.trim().length < 2) {
      setServiceMatches([]);
      setServiceLookupError('');
      setServiceLookupBusy(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setServiceLookupBusy(true);
      setServiceLookupError('');
      try {
        const response = await api.get('/nfse/integration/service-catalog',
          { params: { q: serviceSearch.trim() } });
        if (!cancelled) setServiceMatches(response.data?.results || []);
      } catch (failure) {
        if (!cancelled) {
          setServiceMatches([]);
          setServiceLookupError(requestError(failure));
        }
      } finally {
        if (!cancelled) setServiceLookupBusy(false);
      }
    }, 380);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [activeView, serviceSearch]);

  const selectOfficialService = async (item) => {
    if (serviceSaving) return;
    if (!window.confirm('Definir ' + item.code + ' — ' + item.description +
      ' como código padrão do emitente? Confirme que descreve o serviço efetivamente prestado.')) return;
    setServiceSaving(true);
    setError('');
    try {
      const response = await api.put('/nfse/integration/service-default', { code: item.code });
      setCompany(prev => prev ? { ...prev, defaultServiceCode: response.data.code } : prev);
      setServiceMatches([]);
      setServiceSearch('');
      if (response.data?.report) {
        setPreflight(response.data.report);
        setPreflightNeedsRefresh(false);
      } else {
        setPreflightNeedsRefresh(true);
      }
      try {
        const status = await api.get('/nfse/integration/status');
        setIntegration(status.data || null);
      } catch { /* Sem alterar o status do código salvo. */ }
      setFeedback('Código nacional confirmado e salvo. O diagnóstico foi atualizado sem transmitir notas.');
    } catch (failure) {
      setError(requestError(failure));
    } finally {
      setServiceSaving(false);
    }
  };

  const removeCertificate = async () => {
    if (!window.confirm('Remover o certificado A1 armazenado? As consultas fiscais poderão parar de funcionar.')) return;
    setCertificateSaving(true);
    setError('');
    try {
      await api.delete('/nfse/integration/certificate');
      setCertificatePassword('');
      setCertificateFile(null);
      setPreflight(null);
      setFeedback('Certificado A1 removido. A pré-homologação precisará ser executada com um novo certificado.');
      await load();
    } catch (failure) {
      setError(requestError(failure));
    } finally {
      setCertificateSaving(false);
    }
  };

  const consultDps = async (method) => {
    setDpsBusy(true);
    setDpsResult(null);
    setError('');
    try {
      const response = await api.post(method === 'lookup' ? '/nfse/dps/lookup' : '/nfse/dps/check', {
        series: dpsSeries, number: dpsNumber,
      });
      setDpsResult({ type: method, value: response.data });
    } catch (failure) {
      setError(requestError(failure));
    } finally {
      setDpsBusy(false);
    }
  };

  const processFieldLabels = [
    ['customerName', 'Nome do cliente *'],
    ['customerTaxId', 'CPF/CNPJ do cliente *'],
    ['customerEmail', 'E-mail do cliente'],
    ['serviceAmount', 'Valor do serviço *'],
    ['serviceCode', 'Código do serviço (ou padrão cadastral)'],
    ['serviceDescription', 'Descrição do serviço (ou padrão cadastral)'],
    ['competenceDate', 'Data de competência'],
  ];
  const readProcessFields = async () => {
    setError('');
    setProcessInfo(null);
    setProcessMapping({});
    setProcessTemplateId('');
    if (!/^\d+$/.test(processId) || Number(processId) <= 0) {
      setError('Informe o número de uma instância de processo.');
      return;
    }
    setProcessBusy(true);
    try {
      const response = await api.get(`/nfse/from-process/${processId}/fields`);
      setProcessInfo(response.data);
    } catch (failure) {
      setError(requestError(failure));
    } finally {
      setProcessBusy(false);
    }
  };
  const createProcessDraft = async () => {
    setError('');
    setFeedback('');
    setProcessBusy(true);
    try {
      await api.post(`/nfse/from-process/${processId}`, {
        mapping: processMapping,
        templateId: processTemplateId ? Number(processTemplateId) : undefined,
        defaults: { competenceDate: localDate() },
      });
      setFeedback('Rascunho gerado com os dados do processo. Nenhuma NFS-e foi emitida.');
      setProcessInfo(null);
      await load();
    } catch (failure) {
      setError(requestError(failure));
    } finally {
      setProcessBusy(false);
    }
  };
  const exportVisibleCsv = () => {
    const safe = (value) => {
      let text = String(value ?? '').replace(/"/g, '""');
      if (/^[\s\u0000-\u001f]*[=+@\-]/.test(text)) text = "'" + text;
      return '"' + text + '"';
    };
    const rows = [
      ['Status', 'Tomador', 'CPF/CNPJ', 'Servico', 'Competencia', 'Valor', 'Processo', 'Numero NFS-e', 'Numero DPS', 'Serie DPS'],
      ...filtered.map(note => [
        note.status, fiscalName(note.customerName), note.customerTaxId, note.serviceDescription,
        String(note.competenceDate || '').slice(0, 10), note.serviceAmount,
        note.processInstanceId || '', note.invoiceNumber || '', note.dpsNumber || '', note.dpsSeries || '',
      ]),
    ];
    const csv = '\uFEFF' + rows.map(row => row.map(safe).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'gerenciador-nfs-rascunhos.csv';
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const matchingCustomers = useMemo(() => {
    const search = customerSearch.trim().toLocaleLowerCase('pt-BR');
    const unique = new Map([...customers, ...customerMatches].map(item => [String(item.id), item]));
    const available = Array.from(unique.values()).filter(item => item.status === 'ACTIVE' || String(item.id) === selectedCustomerId);
    return search
      ? available.filter(item => [item.name, item.taxId, item.email, item.phone,
        ...(item.emails || []).map(contact => contact.value || contact),
        ...(item.phones || []).map(contact => contact.value || contact),
      ].some(value => String(value || '').toLocaleLowerCase('pt-BR').includes(search))).slice(0, 60)
      : available.slice(0, 300);
  }, [customers, customerMatches, customerSearch, selectedCustomerId]);

  // Uma pesquisa remota não pode fazer desaparecer o cliente selecionado
  // quando o campo de busca for limpo ou a página abrir um rascunho antigo.
  useEffect(() => {
    if (!selectedCustomerId) {
      setSelectedCustomerCache(null);
      return undefined;
    }
    const local = [...customers, ...customerMatches]
      .find(item => String(item.id) === selectedCustomerId);
    if (local) {
      setSelectedCustomerCache(local);
      return undefined;
    }
    let active = true;
    api.get('/customers/' + selectedCustomerId).then(response => {
      if (active) setSelectedCustomerCache(response.data || null);
    }).catch(() => {
      if (active) setSelectedCustomerCache(null);
    });
    return () => { active = false; };
  }, [selectedCustomerId, customers, customerMatches]);

  const activeCustomer = useMemo(() => {
    const local = [...customers, ...customerMatches]
      .find(item => String(item.id) === selectedCustomerId);
    return local || (String(selectedCustomerCache?.id) === selectedCustomerId ? selectedCustomerCache : null);
  }, [customers, customerMatches, selectedCustomerId, selectedCustomerCache]);
  const descriptionChoices = Array.isArray(activeCustomer?.noteDescriptionPresets)
    ? activeCustomer.noteDescriptionPresets : [];

  const applyDescription = (preset, values = form) => {
    if (!preset) return;
    setSelectedDescriptionName(preset.name);
    setForm(prev => ({ ...prev, serviceDescription: renderNfseCustomerDescription(preset.text, {
      customerName: values.customerName || prev.customerName,
      competenceDate: values.competenceDate || prev.competenceDate,
    }) }));
  };

  const chooseCustomer = (id) => {
    setSelectedCustomerId(id);
    const customer = [...customers, ...customerMatches].find(item => String(item.id) === id);
    setSelectedCustomerCache(customer || null);
    setSelectedDescriptionName('');
    if (!customer) {
      // Remover a seleção não deve deixar os dados de outro tomador no formulário.
      setDraftTemplateId('');
      setForm(prev => ({
        ...prev, customerTaxId: '', customerName: '', customerEmail: '',
        serviceCode: company?.defaultServiceCode || '',
        serviceDescription: company?.defaultServiceDescription || '', serviceAmount: '',
      }));
      return;
    }
    const template = templates.find(item => item.id === customer.defaultTemplateId);
    const primary = Array.isArray(customer.noteDescriptionPresets)
      ? customer.noteDescriptionPresets.find(item => item.isDefault) : null;
    setDraftTemplateId(template ? String(template.id) : '');
    setSelectedDescriptionName(primary?.name || '');
    setForm(prev => ({
      ...prev,
      customerTaxId: customer.taxId || '',
      customerName: customer.name || '',
      customerEmail: customer.email || '',
      serviceCode: customer.defaultServiceCode || template?.serviceCode || company?.defaultServiceCode || '',
      serviceDescription: renderNfseCustomerDescription(primary?.text || customer.defaultServiceDescription || template?.serviceDescription || company?.defaultServiceDescription || '', {
        customerName: customer.name, competenceDate: prev.competenceDate,
      }),
      serviceAmount: customer.defaultAmount || template?.defaultAmount || '',
    }));
  };

  const field = (name) => (event) => {
    const value = name === 'customerName' ? fiscalName(event.target.value) : event.target.value;
    if (name === 'customerTaxId' && selectedCustomerId &&
        value.replace(/\D/g, '') !== String(form.customerTaxId || '').replace(/\D/g, '')) {
      setSelectedCustomerId(''); // Editar documento manualmente desvincula o cliente fiscal.
      setSelectedDescriptionName('');
    }
    if ((name === 'competenceDate' || name === 'customerName') && (selectedDescriptionName || activeCustomer?.defaultServiceDescription)) {
      const preset = selectedDescriptionName ? descriptionChoices.find(item => item.name === selectedDescriptionName) : { text: activeCustomer.defaultServiceDescription };
      if (preset) {
        setForm(prev => {
          const previousText = renderNfseCustomerDescription(preset.text, {
            customerName: prev.customerName, competenceDate: prev.competenceDate,
          });
          const next = { ...prev, [name]: value };
          // Não altera a descrição quando o usuário já personalizou o texto.
          if (prev.serviceDescription === previousText) {
            next.serviceDescription = renderNfseCustomerDescription(preset.text, {
              customerName: next.customerName, competenceDate: next.competenceDate,
            });
          }
          return next;
        });
        return;
      }
    }
    setForm(prev => ({ ...prev, [name]: name === 'customerTaxId'
      ? value.replace(/\D/g, '').slice(0, 14) : value }));
  };
  return (
    <div className="nfse-ui mx-auto w-full max-w-7xl min-w-0 space-y-5 px-1 pb-10 sm:px-2">
      {!configurationOnly && <header className="nfse-header flex flex-col gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-blue-50 p-3 text-blue-600"><FaFileInvoiceDollar size={23} /></div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Gerenciador NFS</h1>
          </div>
        </div>
        <div className="flex w-full gap-2 lg:w-auto">
          <button type="button" onClick={load} disabled={loading} aria-label="Atualizar dados" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 disabled:opacity-50 sm:px-4"><FaSyncAlt className={loading ? 'animate-spin' : ''} /><span className="hidden sm:inline">Atualizar</span></button>
          {(activeView !== 'drafts' || (!showForm && !emissionNote)) && <button type="button" onClick={openNew} className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 sm:flex-none"><FaPlus /> Nova nota</button>}
        </div>
      </header>}

      {!configurationOnly && <nav aria-label="Seções do Gerenciador NFS" className="nfse-nav rounded-2xl border border-slate-100 bg-white p-2 shadow-sm">
        <div className="flex gap-1 overflow-x-auto pb-1 sm:flex-wrap sm:pb-0">
          {NAV_ITEMS.filter(item => ['overview', 'drafts', 'consultations'].includes(item.id)).map(({ id, label, Icon }) => (
            <button key={id} type="button" aria-pressed={activeView === id} onClick={() => switchView(id)}
              className={`inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-4 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 ${activeView === id ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}>
              <Icon aria-hidden="true" size={14}/>{label}
            </button>
          ))}
        </div>
      </nav>}

      {integration?.environment === 'restricted' && <p role="status" className="rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-900">Modo de teste · documentos sem validade fiscal</p>}
      {!configurationOnly && <div className={`nfse-sync ${syncError ? 'nfse-sync-error' : ''}`}>
        <p role="status" className="text-xs text-slate-600">
          {syncBusy ? 'Consultando situação oficial...' : syncedAt ? `Última consulta: ${new Date(syncedAt).toLocaleString('pt-BR')}` : 'Consulta oficial pendente.'}
          {syncPartial && <span className="ml-2">Verificação oficial em andamento{syncPending > 0 ? ` · ${syncPending} documentos pendentes` : ''}.</span>}
        </p>
        {syncError && <details className="nfse-sync-details">
          <summary>Atualização pendente</summary>
          <p className="mt-2 text-xs">{syncError}</p>
          <p className="mt-1 text-xs">Situação anterior preservada.</p>
        </details>}
      </div>}
      {cancelNote && <section role="dialog" aria-modal="true" aria-label="Cancelar NFS-e" className="space-y-3 rounded-xl border border-red-200 bg-white p-5"><form onSubmit={confirmCancellation} className="space-y-3">
        <h2 className="font-semibold">Cancelar NFS-e {cancelNote.invoiceNumber || 'sem número disponível'}</h2>
        <p className="text-sm">O pedido será enviado ao governo. Confirme o motivo e a justificativa para cancelar esta nota.</p>
        <label className="block">Motivo<select value={cancelCode} onChange={e => setCancelCode(e.target.value)} disabled={cancelBusy} className="block w-full rounded border p-2"><option value="1">Erro na emissão</option><option value="2">Serviço não prestado</option><option value="9">Outros</option></select></label>
        <label className="block">Justificativa<textarea required minLength={15} maxLength={255} value={cancelReason} onChange={e => setCancelReason(e.target.value)} disabled={cancelBusy} className="block w-full rounded border p-2" /></label><p className="text-xs">{cancelReason.trim().length}/255 caracteres · mínimo 15</p>
        <button type="button" disabled={cancelBusy} onClick={() => setCancelNote(null)} className="mr-3 rounded border p-2">Voltar</button><button type="submit" disabled={cancelBusy || cancelReason.trim().length < 15} className="rounded bg-red-700 p-2 text-white">{cancelBusy ? 'Aguardando confirmação oficial...' : 'Confirmar cancelamento da NFS-e'}</button>
      </form></section>}
      {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {feedback && <div role="status" className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800">{feedback}</div>}

      {activeView === 'settings' && (
      <details ref={preflightRef} aria-label="Pré-homologação automática NFS-e" className="space-y-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5">
        <summary className="cursor-pointer text-sm font-semibold text-slate-700">Detalhes técnicos da emissão</summary>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Verificação automática da emissão</h2>
            <p className="mt-1 text-xs text-slate-600">Após salvar o A1, o sistema verifica os dados fiscais, a assinatura digital e a conexão de consulta com a SEFIN.</p>
          </div>
          <button type="button" onClick={runPreflight}
            disabled={preflightBusy || certificateSaving || !certificate?.usable}
            className="min-h-[44px] rounded-xl border border-blue-200 px-4 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-40">
            {preflightBusy ? 'Verificando...' : 'Reexecutar verificações'}
          </button>
        </div>
        {!preflight && <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
          {certificate?.configured ? 'Nenhuma verificação foi salva ainda. Clique em Reexecutar verificações.' : 'Cadastre o certificado A1 acima para iniciar automaticamente.'}
        </p>}
        {preflight && <>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${preflight.status === 'blocked' ? 'bg-amber-100 text-amber-900' : 'bg-blue-50 text-blue-900'}`}>
              {preflight.status === 'blocked' ? 'Correções necessárias' : integration?.environment === 'production' ? 'Verificações concluídas para produção' : 'Verificações concluídas no ambiente restrito'}
            </span>
            <span className="text-xs text-slate-500">Verificado em {new Date(preflight.checkedAt).toLocaleString('pt-BR')}</span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {(preflight.checks || []).map(check => <div key={check.id}
              className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
              <div className="flex items-center gap-2">
                <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${check.status === 'passed' ? 'bg-emerald-500' : check.status === 'failed' ? 'bg-red-500' : 'bg-amber-500'}`}/>
                <strong className="text-xs text-slate-900">{check.label}</strong>
              </div>
              <p className="mt-1 text-xs text-slate-600">{check.message}</p>
              {check.fix && <p className="mt-1.5 text-xs font-medium text-blue-800">Como corrigir: {check.fix}</p>}
              {check.id === 'service' && check.status !== 'passed' && (
                <div className="mt-3 space-y-2">
                  {(check.suggestions || []).length > 0 && <p className="rounded-lg bg-blue-50 p-2.5 text-xs font-medium leading-relaxed text-blue-900">
                    O iBPM encontrou {check.suggestions.length} opções a partir das atividades e documentos cadastrados. Selecione abaixo apenas o serviço efetivamente prestado — não é necessário digitar ou consultar outro site.
                  </p>}
                  <label className="block text-xs font-semibold text-slate-800" htmlFor="nfse-service-search">
                    Buscar outro serviço, se necessário
                  </label>
                  <input id="nfse-service-search" value={serviceSearch}
                    onChange={event => setServiceSearch(event.target.value)}
                    placeholder="Ex.: desenvolvimento, suporte, sites, consultoria"
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-base sm:text-sm" />
                  {serviceLookupBusy && <p className="text-xs text-slate-500">Consultando lista oficial...</p>}
                  {serviceLookupError && <p role="alert" className="text-xs text-amber-800">{serviceLookupError}</p>}
                  {(serviceSearch.trim().length >= 2 ? serviceMatches : check.suggestions || []).length > 0 &&
                    <div className="max-h-60 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1">
                      {(serviceSearch.trim().length >= 2 ? serviceMatches : check.suggestions || []).map(item =>
                        <div key={item.code} className="flex items-center justify-between gap-2 rounded-md px-2 py-2 hover:bg-slate-50">
                          <span className="text-xs text-slate-700"><strong>{item.code}</strong> — {item.description}</span>
                          <button type="button" disabled={serviceSaving} onClick={() => selectOfficialService(item)}
                            className="shrink-0 rounded-md border border-blue-200 px-2 py-2 text-xs font-semibold text-blue-700 disabled:opacity-50">
                            {serviceSaving ? 'Salvando...' : 'Selecionar'}
                          </button>
                        </div>)}
                    </div>}
                  <a href="https://www.gov.br/nfse/pt-br/mei-e-demais-empresas/codigos-de-tributacao-nacional-nbs"
                    target="_blank" rel="noopener noreferrer" className="inline-block text-xs text-blue-700 underline">
                    Conferir lista nacional do Governo Federal
                  </a>
                  <p className="text-xs text-slate-500">A escolha é feita uma única vez como serviço padrão; o iBPM reutiliza esse código nas próximas notas. Para outro tipo de serviço, confirme o código específico ao criar a nota.</p>
                </div>)}
              {check.id === 'layout' && <a href="https://www.gov.br/nfse/pt-br/biblioteca/documentacao-tecnica/atualizacoes-e-implantacoes"
                target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-block text-xs text-blue-700 underline">
                Ver atualizações oficiais identificadas
              </a>}
            </div>)}
          </div>
          {preflight.safeToTestInRestricted && integration?.environment !== 'production' && <p className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
            A etapa técnica foi concluída. O teste oficial de homologação fica disponível em Rascunhos → Conferir DPS. Confirme os dados e tributos antes de enviar ao ambiente restrito, sem efeito fiscal.
          </p>}
          <p className="text-xs leading-relaxed text-slate-500">{preflight.note}</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => switchView('drafts')}
              className="min-h-[44px] rounded-xl border border-slate-200 px-4 text-xs font-semibold text-slate-700">Ir para notas</button>
            <button type="button" onClick={() => window.open('https://www.gov.br/nfse/pt-br/biblioteca/documentacao-tecnica/apis-prod-restrita-e-producao', '_blank', 'noopener,noreferrer')}
              className="min-h-[44px] rounded-xl border border-slate-200 px-4 text-xs text-slate-700">Consultar API oficial</button>
          </div>
        </>}
      </details>
      )}

      {activeView === 'settings' && (
      <section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">Certificado digital A1 <InfoTip text="O certificado permite autenticar consultas oficiais. Arquivo e senha ficam criptografados no servidor; emissão exige homologação."/></h2>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 p-3 text-sm">
          <p className="text-slate-700">
            <strong>{certificate?.configured ? 'A1 armazenado' : integration?.credentialSource === 'environment' ? 'A1 configurado no servidor' : 'A1 não configurado'}</strong>
            {certificate?.label && <span className="ml-2 text-slate-500">· {certificate.label}</span>}
            {certificate?.configured && !certificate?.encryptionAvailable && <span className="ml-2 text-xs font-semibold text-amber-700">· Aguardando chave de criptografia no servidor</span>}
            {certificate?.expiresAt && <span className={`ml-2 text-xs ${certificate.validNow ? 'text-slate-600' : 'font-semibold text-red-700'}`}>· {certificate.validNow ? 'Válido até' : 'Vencido em'} {new Date(certificate.expiresAt).toLocaleDateString('pt-BR')}</span>}
          </p>
          {certificate?.configured && <button type="button" disabled={certificateSaving} onClick={removeCertificate} className="rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-700 disabled:opacity-40">Revogar configuração</button>}
        </div>
        {certificate && !certificate.encryptionAvailable && (
          <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 md:text-sm">
            A proteção do certificado ainda não está configurada no servidor. O cadastro permanece bloqueado até o deploy
            inicializar a chave exclusiva e persistente do A1. Não informe o segredo do servidor nem a senha do certificado neste aviso.
          </p>
        )}
        <form onSubmit={saveCertificate} className="grid gap-3 md:grid-cols-2">
          <label className="text-sm font-medium text-slate-700">Arquivo do certificado (.pfx / .p12)
            <input type="file" accept=".pfx,.p12,application/x-pkcs12" required onChange={event => setCertificateFile(event.target.files?.[0] || null)} className="mt-1 block w-full text-xs text-slate-600"/>
          </label>
          <label className="text-sm font-medium text-slate-700">Senha do certificado
            <input type="password" autoComplete="off" value={certificatePassword} onChange={event => setCertificatePassword(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Senha do PFX"/>
          </label>
          <label className="text-sm font-medium text-slate-700 md:col-span-2">Descrição (opcional)
            <input value={certificateLabel} onChange={event => setCertificateLabel(event.target.value)} maxLength={100} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Ex.: Certificado A1 da empresa"/>
          </label>
          <div className="md:col-span-2 flex flex-wrap items-center gap-3">
            <button type="submit" disabled={certificateSaving || !certificateFile || (certificate && !certificate.encryptionAvailable)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">{certificateSaving ? 'Verificando A1...' : certificate?.configured ? 'Substituir certificado A1' : 'Cadastrar certificado A1'}</button>
            <InfoTip text="A chave privada do A1 é mantida no servidor e provisionada automaticamente no deploy; o arquivo PFX e a senha são armazenados criptografados. A validade do PFX não comprova credenciamento fiscal."/>
          </div>
        </form>
      </section>
      )}

      {activeView === 'overview' && <>
      <div className="nfse-metrics grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[
          ['Rascunhos', pending.length],
          ['Emitidas e autorizadas', issued.length],
          ['Total autorizado', money(issued.reduce((sum, note) => sum + Number(note.serviceAmount || 0), 0))],
        ].map(([label, value]) => <div key={label} className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm"><p className="text-sm text-slate-500">{label}</p><p className="mt-2 text-2xl font-semibold text-slate-900">{value}</p></div>)}
      </div>

      <div className="nfse-company rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
          <div>
            <p className="font-semibold text-slate-800">{fiscalName(company?.name) || 'Empresa não cadastrada'}</p>
          </div>
          {openTab && <button type="button" onClick={() => openRegistration('fiscal')} className="rounded-lg border border-blue-200 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50">Dados Cadastrais</button>}
        </div>
      </div>

      <div className="nfse-shortcuts grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <button type="button" onClick={() => switchView('drafts')} className="flex min-h-[84px] items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-left transition hover:border-blue-300 hover:bg-blue-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600">
          <span className="rounded-xl bg-blue-600 p-3 text-white"><FaFileInvoiceDollar size={16}/></span>
          <span><strong className="block text-sm font-semibold text-slate-900">Ver documentos</strong></span>
        </button>
        <a href="https://www.nfse.gov.br/EmissorNacional/Login" target="_blank" rel="noopener noreferrer"
          className="flex min-h-[84px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-blue-300 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600">
          <span className="rounded-xl bg-slate-100 p-3 text-slate-700"><FaFileInvoiceDollar size={16}/></span>
          <span><strong className="block text-sm font-semibold text-slate-900">Abrir emissor oficial</strong>

          </span>
        </a>
        <button type="button" onClick={() => setActiveView('consultations')} className="flex min-h-[84px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-blue-300 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600">
          <span className="rounded-xl bg-slate-100 p-3 text-slate-700"><FaTasks size={16}/></span>
          <span><strong className="block text-sm font-semibold text-slate-900">Consultar notas</strong></span>
        </button>
      </div>
      </>}
      {activeView === 'automations' && (
      <section className="space-y-4 rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-slate-900">Agendamentos <InfoTip text="Vincule agendamentos do BPMN aos campos de formulário para gerar somente rascunhos, nunca notas fiscais reais."/></h2>
        </div>
        <label className="block text-sm font-medium text-slate-700">Agendamento
          <select value={selectedScheduleId} onChange={event => selectSchedule(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Selecione um agendamento ativo</option>
            {schedules.filter(item => item.active).map(item => <option value={item.id} key={item.id}>#{item.id} · {item.processName} ({item.type})</option>)}
          </select>
        </label>
        {selectedSchedule && <div className="space-y-4 rounded-lg border border-blue-100 bg-blue-50/40 p-4">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
            <input type="checkbox" checked={Boolean(scheduleRule.enabled)} onChange={event => setScheduleRule(prev => ({ ...prev, enabled: event.target.checked }))}/>
            Criar rascunho de NFS-e quando os dados do processo estiverem completos
          </label>
          <label className="block text-xs font-medium text-slate-700">Modelo de serviço (opcional)
            <select value={scheduleRule.templateId || ''} onChange={event => setScheduleRule(prev => ({ ...prev, templateId: event.target.value ? Number(event.target.value) : null }))} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">Usar serviço e descrição dos Dados Cadastrais</option>
              {templates.map(template => <option key={template.id} value={template.id}>{template.name}</option>)}
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            {processFieldLabels.map(([key, label]) => <label key={key} className="text-xs font-medium text-slate-700">
              {label}
              <input list="nfse-schedule-fields" placeholder="ID do campo do formulário" value={scheduleRule.mapping?.[key] || ''} onChange={event => setScheduleRule(prev => ({ ...prev, mapping: { ...prev.mapping, [key]: event.target.value } }))} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
            </label>)}
            <datalist id="nfse-schedule-fields">{(selectedSchedule.fields || []).map(fieldId => <option key={fieldId} value={fieldId}/>)}</datalist>
          </div>
          <InfoTip text="Preencha os IDs exatos dos campos, conforme os formulários. Desabilitar a geração de rascunho não cancela o agendamento."/>
          <button type="button" onClick={saveSchedule} disabled={savingSchedule} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">{savingSchedule ? 'Salvando...' : 'Salvar automação de rascunho'}</button>
        </div>}
      </section>
      )}

      {activeView === 'consultations' && (
      <section className="space-y-3 rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
        <h2 className="flex items-center gap-2 font-semibold text-slate-900">Consultar DPS <InfoTip text="Informe série e número; CNPJ e município vêm de Dados Cadastrais. Consulta negativa não autoriza nova emissão."/></h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="min-w-0 text-xs font-medium text-slate-700">Série da DPS
            <input inputMode="numeric" value={dpsSeries} onChange={event => setDpsSeries(event.target.value.replace(/\D/g,'').slice(0,5))} placeholder="Ex.: 90000" className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"/>
          </label>
          <label className="min-w-0 text-xs font-medium text-slate-700">Número da DPS
            <input inputMode="numeric" value={dpsNumber} onChange={event => setDpsNumber(event.target.value.replace(/\D/g,'').slice(0,15))} placeholder="Ex.: 1001" className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"/>
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={!integration?.consultationAvailable || dpsBusy || !dpsSeries || !dpsNumber} onClick={() => consultDps('check')} className="rounded-lg border border-blue-200 px-4 py-2 text-sm font-semibold text-blue-700 disabled:opacity-40">Verificar existência</button>
          <button type="button" disabled={!integration?.consultationAvailable || dpsBusy || !dpsSeries || !dpsNumber} onClick={() => consultDps('lookup')} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">Consultar chave</button>
        </div>
        {dpsResult && <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
          <p className="font-medium">Identificador: <code className="break-all text-xs">{dpsResult.value.id}</code></p>
          {dpsResult.type === 'check' ? <p>{dpsResult.value.exists ? 'A SEFIN informou que uma nota já existe para esta DPS.' : 'A SEFIN não encontrou a DPS. Isso não autoriza uma nova emissão sem conferência.'}</p> : <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all text-xs">{JSON.stringify(dpsResult.value.result, null, 2)}</pre>}
        </div>}
      </section>
      )}

      {activeView === 'overview' && readiness?.missing?.length > 0 && <section className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <h2 className="text-sm font-semibold text-amber-900">Dados pendentes para configuração da NFS-e</h2>
        <ul className="list-inside list-disc space-y-1 text-sm text-amber-900">
          {readiness.missing.map(item => <li key={item.field}>{item.label} <span className="text-xs opacity-70">({item.section})</span></li>)}
        </ul>
        <p className="text-xs text-amber-800">Esta é uma pré-verificação. Não substitui a validação fiscal oficial nem habilita a emissão.</p>
      </section>}

      {activeView === 'consultations' && (
      <section className="space-y-3 rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
        <div>
          <h2 className="font-semibold text-slate-900">Consulta oficial da NFS-e</h2>
          <p className="text-xs text-slate-500">
            Ambiente: {integration?.environment === 'production' ? 'produção' : 'homologação'} · Certificado A1: {integration?.certificateConfigured ? 'configurado' : 'não configurado'}
          </p>
        </div>
        <InfoTip text="Consulta oficial por chave de acesso, sem emitir nova NFS-e."/>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input aria-label="Chave de acesso da NFS-e" inputMode="numeric" maxLength={50} value={accessKey} onChange={event => setAccessKey(event.target.value.replace(/\D/g, '').slice(0, 50))} placeholder="Chave de acesso (50 dígitos)" className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <button type="button" disabled={!integration?.consultationAvailable || consulting || accessKey.length !== 50} onClick={() => consultOfficial('nfse')} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">Consultar nota</button>
          <button type="button" disabled={!integration?.consultationAvailable || consulting || accessKey.length !== 50} onClick={() => consultOfficial('events')} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold disabled:opacity-40">Consultar eventos</button>
        </div>
        {!integration?.consultationAvailable && <p className="text-xs text-amber-800">Configure o certificado A1 no servidor para habilitar consultas. Nenhum certificado é enviado pelo navegador.</p>}
        {officialResult && <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
          <p className="mb-2 text-xs font-semibold text-blue-900">{officialResult.type} · Resposta oficial</p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all text-xs text-slate-700">{JSON.stringify(officialResult.data, null, 2)}</pre>
        </div>}
      </section>
      )}

      {activeView === 'automations' && (
      <section className="space-y-4 rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
        <div>
          <h2 className="font-semibold text-slate-900">Criar a partir de formulário BPMN</h2>
          <InfoTip text="Localize uma solicitação e mapeie os campos do formulário para preencher o rascunho." />
        </div>
        <div className="flex flex-wrap gap-2">
          <input aria-label="Número da instância de processo" value={processId} onChange={event => { setProcessId(event.target.value.replace(/\D/g, '')); setProcessInfo(null); }} placeholder="Número da solicitação / instância" inputMode="numeric" className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <button type="button" disabled={processBusy || !processId} onClick={readProcessFields} className="rounded-lg border border-blue-200 px-4 py-2 text-sm font-semibold text-blue-700 disabled:opacity-40">Localizar formulário</button>
        </div>
        {processInfo && <div className="space-y-3 rounded-xl border border-blue-100 bg-blue-50/40 p-4">
          <p className="text-sm font-semibold text-slate-800">Processo: {processInfo.processName || processInfo.id}</p>
          {templates.length > 0 && <label className="block text-xs font-medium text-slate-700">Modelo de serviço (opcional)
            <select value={processTemplateId} onChange={event => setProcessTemplateId(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">Utilizar padrões de Dados Cadastrais</option>
              {templates.map(template => <option key={template.id} value={template.id}>{template.name}</option>)}
            </select>
          </label>}
          <div className="grid gap-3 sm:grid-cols-2">
            {processFieldLabels.map(([key, label]) => <label key={key} className="text-xs font-medium text-slate-700">
              {label}
              <select value={processMapping[key] || ''} onChange={event => setProcessMapping(prev => ({ ...prev, [key]: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="">Usar padrão / não informar</option>
                {processInfo.fields.map(name => <option key={name} value={name}>{name}</option>)}
              </select>
            </label>)}
          </div>
          <p className="text-xs text-slate-500">Se não houver campo de competência, será utilizada a data atual. O código e a descrição do serviço utilizarão os padrões de Dados Cadastrais.</p>
          <button type="button" disabled={processBusy || !processMapping.customerName || !processMapping.customerTaxId || (!processMapping.serviceAmount && !templates.some(template => String(template.id) === processTemplateId && template.defaultAmount))} onClick={createProcessDraft} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">Gerar rascunho do processo</button>
        </div>}
      </section>
      )}

      {activeView === 'templates' && (
      <section className="space-y-4 rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">Padrões gerais <InfoTip text="Salve modelos reutilizáveis. Para definir valores e serviços específicos, use o cadastro de Clientes."/></h2>
        </div>
        {templates.length > 0 && <div className="flex flex-wrap gap-2">
          {templates.map(template => <div key={template.id} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
            <button type="button" title="Editar modelo" onClick={() => {
              setEditingTemplateId(template.id);
              setTemplateForm({ name: template.name, serviceCode: template.serviceCode, serviceDescription: template.serviceDescription, defaultAmount: template.defaultAmount || '' });
            }} className="rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-white">{template.name}</button>
            <button type="button" title="Excluir modelo" onClick={() => deleteTemplate(template)} disabled={templateBusy} className="rounded-md px-2 py-2 text-xs text-red-500 hover:bg-red-50 disabled:opacity-40"><FaTrashAlt /></button>
          </div>)}
        </div>}
        <form onSubmit={saveTemplate} className="grid gap-3 md:grid-cols-2">
          <label className="text-xs font-medium text-slate-700">Nome do modelo
            <input required maxLength={100} value={templateForm.name} onChange={event => setTemplateForm(prev => ({ ...prev, name: event.target.value }))} placeholder="Ex.: Mensalidade de suporte" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <label className="text-xs font-medium text-slate-700">Código do serviço
            <input required maxLength={20} value={templateForm.serviceCode} onChange={event => setTemplateForm(prev => ({ ...prev, serviceCode: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <label className="text-xs font-medium text-slate-700">Valor padrão (opcional)
            <input type="number" min="0.01" step="0.01" value={templateForm.defaultAmount} onChange={event => setTemplateForm(prev => ({ ...prev, defaultAmount: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <label className="text-xs font-medium text-slate-700 md:col-span-2">Descrição do serviço
            <textarea required maxLength={4000} value={templateForm.serviceDescription} onChange={event => setTemplateForm(prev => ({ ...prev, serviceDescription: event.target.value }))} rows={2} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <div className="flex flex-wrap gap-2 md:col-span-2">
            <button type="submit" disabled={templateBusy} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">{editingTemplateId ? 'Atualizar modelo' : 'Salvar modelo'}</button>
            {editingTemplateId && <button type="button" onClick={() => { setEditingTemplateId(null); setTemplateForm(emptyTemplate()); }} className="rounded-lg border border-slate-200 px-4 py-2 text-sm">Cancelar edição</button>}
          </div>
        </form>
      </section>
      )}

      {activeView === 'drafts' && showForm && (
        <form onSubmit={saveDraft} className="nfse-draft space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="font-semibold text-slate-900">{editingDraftId ? 'Editar nota' : 'Nova nota'}</h2><button type="button" onClick={closeDraft} className="min-h-[44px] rounded-xl border border-slate-200 px-4 text-sm font-medium text-slate-700">Fechar</button></div>
          <div className="nfse-customer-picker grid min-w-0 gap-4 sm:grid-cols-2">
            <label className="block min-w-0 text-sm font-medium text-slate-700">Buscar cliente
              <input value={customerSearch} onChange={event => setCustomerSearch(event.target.value)}
                placeholder="Nome ou CPF/CNPJ" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              {customerSearchBusy && <span className="mt-1 block text-xs text-slate-500">Buscando em todos os clientes...</span>}
            </label>
            <label className="block min-w-0 text-sm font-medium text-slate-700">Cliente cadastrado
              <select value={selectedCustomerId} onChange={event => chooseCustomer(event.target.value)}
                className="mt-1 w-full rounded-xl border border-blue-100 bg-white px-3 py-2 text-sm">
                <option value="">Cliente avulso / preenchimento manual</option>
                {matchingCustomers.map(item =>
                  <option key={item.id} value={item.id}>{fiscalName(item.name)}{item.taxId ? ' · ' + item.taxId : ''}{item.status === 'INACTIVE' ? ' (inativo)' : ''}</option>)}
                {selectedCustomerId && !matchingCustomers.some(item => String(item.id) === selectedCustomerId) &&
                  <option value={selectedCustomerId}>Cliente selecionado</option>}
              </select>
            </label>
          </div>
          {openTab && <button type="button" onClick={() => { if (!draftIsDirty() || window.confirm('Descartar o rascunho não salvo para abrir Clientes?')) openRegistration('customers'); }} className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">Dados do cliente</button>}

          <div className="grid gap-4 sm:grid-cols-2">
            {[
              ['Nome/Razão social do tomador', 'customerName', 'text', true],
              ['CPF/CNPJ do tomador', 'customerTaxId', 'text', true],
              ['E-mail do cliente', 'customerEmail', 'email', false],
              ['Valor (R$)', 'serviceAmount', 'number', true],
              ['Competência', 'competenceDate', 'date', true],
            ].map(([label, name, type, required]) =>
              <label key={name} className={`text-sm font-medium text-slate-700 ${name === 'customerEmail' ? 'sm:col-span-2' : ''}`}>{label}
                <input type={name === 'serviceAmount' ? 'text' : type}
                  placeholder={name === 'serviceAmount' ? 'Ex.: 250,00' : undefined} value={name === 'customerTaxId' ? formatDocument(form[name]) : name === 'customerName' ? fiscalName(form[name]) : form[name]}
                  onChange={field(name)} inputMode={name === 'customerTaxId' ? 'numeric' : name === 'serviceAmount' ? 'decimal' : undefined}
                  maxLength={name === 'customerTaxId' ? 18 : name === 'customerName' ? 200 : name === 'serviceCode' ? 20 : undefined}
                  required={required} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
              </label>)}
            {(descriptionChoices.length > 0 || templates.length > 0) && <div className="space-y-4 sm:col-span-2">
              {descriptionChoices.length > 0 && <div className="nfse-description-presets space-y-2">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <label className="min-w-0 flex-1 text-xs font-semibold text-slate-700">Modelo de descrição
                    <select value={selectedDescriptionName} onChange={event => {
                      const name = event.target.value;
                      if (!name) { setSelectedDescriptionName(''); return; }
                      const preset = descriptionChoices.find(item => item.name === name);
                      if (preset) applyDescription(preset);
                    }} className="mt-1 w-full rounded-xl border border-blue-200 bg-white px-3 py-2 text-sm">
                      <option value="">Descrição manual</option>
                      {descriptionChoices.map((preset, index) =>
                        <option key={index} value={preset.name}>{preset.name}{preset.isDefault ? ' · Principal' : ''}</option>)}
                    </select>
                  </label>
                  <button type="button" disabled={!selectedDescriptionName}
                    onClick={() => applyDescription(descriptionChoices.find(item => item.name === selectedDescriptionName))}
                    className="min-h-[44px] rounded-xl border border-blue-200 bg-white px-3 text-sm font-semibold text-blue-700 disabled:opacity-40">Reaplicar padrão</button>
                </div>
              </div>}
              {templates.length > 0 && <label className="block text-sm font-medium text-slate-700">Utilizar modelo de serviço
                <select value={draftTemplateId} onChange={event => {
                  setDraftTemplateId(event.target.value);
                  setSelectedDescriptionName('');
                  const template = templates.find(item => String(item.id) === event.target.value);
                  if (template) setForm(prev => ({
                    ...prev, serviceCode: template.serviceCode, serviceDescription: template.serviceDescription,
                    serviceAmount: template.defaultAmount || prev.serviceAmount,
                  }));
                }} className="mt-1 w-full rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm">
                  <option value="">Selecione um modelo</option>
                  {templates.map(template => <option key={template.id} value={template.id}>{template.name}</option>)}
                </select>
              </label>}
            </div>}
            <label className="text-sm font-medium text-slate-700 sm:col-span-2">Descrição do serviço
              <textarea value={form.serviceDescription} onChange={field('serviceDescription')} required rows={3} maxLength={4000}
                placeholder="Descreva claramente os serviços prestados." className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm leading-relaxed" />
              <span className="mt-1 block text-right text-[11px] font-normal text-slate-500">{String(form.serviceDescription || '').length}/4000</span>
            </label>
          </div>
          <details className="nfse-service-details rounded-lg border p-3"><summary className="cursor-pointer text-sm">Serviço · {form.serviceCode || 'não definido'}</summary><NfseServicePicker value={form.serviceCode} onChange={code => setForm(previous => ({ ...previous, serviceCode: code }))} /></details>
          <div className="nfse-form-actions flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={closeDraft} className="min-h-[44px] rounded-xl border border-slate-200 px-4 py-2 text-sm">Cancelar</button>
            <button type="submit" value="draft" disabled={saving} className="min-h-[44px] rounded-xl border border-slate-200 px-4 text-sm disabled:opacity-50">Salvar rascunho</button>
            <button type="submit" value="issue" disabled={saving} className="min-h-[44px] rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white disabled:opacity-50">{saving ? 'Preparando...' : 'Continuar para emissão'}</button>
          </div>
        </form>
      )}

      {activeView === 'drafts' && emissionNote && <section aria-label="Conferência de emissão de NFS-e" className="nfse-emission space-y-4 rounded-2xl border border-blue-200 bg-blue-50/30 p-4 shadow-sm sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div><h2 className="text-lg font-semibold text-slate-900">Emitir nota</h2>
            <p className="mt-1 text-xs text-slate-600">Tomador: {fiscalName(emissionNote.customerName)} · {money(emissionNote.serviceAmount)} · Competência {dateLabel(emissionNote.competenceDate)}</p>
          </div>
          <button type="button" disabled={emissionBusy} onClick={() => { ++emissionPrefillRequest.current; setEmissionNote(null); setEmissionPreview(null); }} className="min-h-[44px] rounded-xl border border-slate-200 bg-white px-4 text-sm">Fechar</button>
        </div>

        {emissionPrefillNotice && <p className="text-xs text-slate-500">{emissionPrefillNotice}</p>}
        {(postalBusy || postalNotice) && <p role="status" className="text-xs text-blue-700">{postalBusy ? 'Consultando endereço pelo CEP...' : postalNotice}</p>}
        {emissionDefaultsBusy ? <p role="status" className="text-sm text-blue-700">Preparando sua nota...</p> : <>
          <div className="flex flex-wrap items-center gap-3"><button type="button" onClick={refreshDpsSequence} disabled={sequenceBusy || emissionBusy} className="min-h-[44px] rounded-lg border border-blue-200 px-3 text-xs font-semibold text-blue-700 disabled:opacity-40">{sequenceBusy ? 'Consultando sequência DPS...' : 'Atualizar sequência DPS'}</button></div>
          {!String(emissionForm.simplesRate || '').trim() && <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">A alíquota padrão da empresa ainda não foi definida. <button type="button" onClick={() => openRegistration('fiscal')} className="font-semibold underline">Preencher em Dados Cadastrais</button></div>}
          {emissionMissing.length > 0 && <p className="text-sm font-medium text-amber-900">Complete somente os dados da nota que não foram encontrados no cadastro.</p>}
          <details className="rounded-lg border p-3"><summary className="cursor-pointer text-sm">Alterar alíquota ou serviço somente nesta nota</summary><p className="my-2 text-xs">Estas alterações não modificam os padrões da empresa.</p><label className="block text-sm">Alíquota efetiva (%)<input inputMode="decimal" value={emissionForm.simplesRate} onChange={e => { rateEdited.current = true; updateEmissionField('simplesRate', e.target.value); }} className="block w-full rounded border p-2" /></label><NfseServicePicker value={emissionNote.serviceCode} onChange={changeEmissionService} disabled={serviceOverrideBusy || emissionBusy} />{serviceOverrideBusy && <p role="status">Atualizando serviço da nota...</p>}</details>
          <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={emissionDetails} onChange={e => setEmissionDetails(e.target.checked)} />Ver ou alterar todos os dados fiscais</label>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {EMISSION_FIELDS.filter(([, key]) => emissionDetails || emissionMissing.includes(key)).map(([label, key, placeholder]) => <label key={key} className="text-xs font-medium text-slate-800">
              {label}
              <input required readOnly={['series', 'number'].includes(key)} value={emissionForm[key]} placeholder={placeholder}
                onChange={event => updateEmissionField(key, event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-base sm:text-sm" />
            </label>)}
          </div>
          {emissionPreviewBusy && <p role="status" className="text-sm text-blue-700">Conferindo sua nota...</p>}
        </>}
        {emissionPreview && <div className="space-y-3 rounded-xl border border-blue-200 bg-white p-4" role="region" aria-label="Prévia da DPS">
          <p className="text-sm font-semibold text-slate-900">Nota pronta para sua confirmação</p>
          <p className="text-xs text-slate-700">{emissionPreview.alert}</p>
          <dl className="grid gap-2 text-xs sm:grid-cols-2">
            <div><dt className="text-slate-500">Tomador</dt><dd>{fiscalName(emissionPreview.summary.customerName)}</dd></div>
            <div><dt className="text-slate-500">Serviço</dt><dd>{emissionPreview.summary.serviceCode}</dd></div>
            <div><dt className="text-slate-500">Valor</dt><dd>{money(emissionPreview.summary.serviceAmount)}</dd></div>
            <div><dt className="text-slate-500">Ambiente</dt><dd>{emissionPreview.environment === 'production' ? 'PRODUÇÃO REAL' : 'Produção restrita (testes)'}</dd></div>
          </dl>
          <details className="text-xs text-slate-600"><summary className="cursor-pointer">Dados fiscais utilizados</summary>
            <dl className="mt-2 grid gap-2 sm:grid-cols-2">{EMISSION_FIELDS.map(([label, key]) => <div key={key}><dt>{label}</dt><dd className="font-semibold">{emissionForm[key]}</dd></div>)}</dl>
            <p className="mt-2">Alíquota utilizada nesta nota: {emissionForm.simplesRate}% · Simples Nacional · ISS não retido.</p>
          </details>
          <button type="button" onClick={sendEmission} disabled={!integration?.emissionAvailable || emissionBusy}
            className="min-h-[44px] rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40">
            {emissionBusy ? 'Transmitindo...' : emissionPreview.environment === 'production' ? 'Emitir nota' : 'Emitir nota de teste'}
          </button>
          {!integration?.emissionAvailable && <p className="text-xs text-amber-800">
            Não foi possível liberar a emissão. Confira a validade do A1 e as pendências fiscais da empresa em Detalhes.
          </p>}
        </div>}
      </section>}

      {activeView === 'drafts' && !showForm && !emissionNote && (
      <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div><h2 className="text-lg font-semibold text-slate-900">Documentos registrados</h2><p className="mt-1 text-xs text-slate-500">{filtered.length} resultados carregados · limite de 200. Rascunhos não possuem validade fiscal.</p></div>
          <button type="button" disabled={filtered.length === 0} onClick={exportVisibleCsv} title="Exporta até 200 documentos carregados; não é relatório fiscal oficial" className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-40">Exportar CSV (até 200)</button>
          <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
            <label className="sr-only" htmlFor="nfse-document-status">Filtrar por status</label>
            <select id="nfse-document-status" value={statusFilter} onChange={event => setStatusFilter(event.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm sm:w-40">
              <option value="all">Todos os status</option>
              <option value="DRAFT">Rascunhos</option>
              <option value="AUTHORIZED">Autorizadas</option>
              <option value="EXTERNAL">Emitidas fora do iBPM</option>
              <option value="TEST_AUTHORIZED">Testes autorizados (sem valor fiscal)</option>
              <option value="PROCESSING">Processando</option>
              <option value="REJECTED">Rejeitadas</option>
              <option value="CANCELED">Canceladas</option>
            </select>
            <input aria-label="Pesquisar documentos" value={query} onChange={event => setQuery(event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm sm:w-72" placeholder="Buscar cliente, documento ou serviço" />
          </div>
        </div>
        <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-900">Notas anteriores</h3>
            <button type="button" disabled={historyBusy || !integration?.consultationAvailable || integration?.environment !== 'production'}
              onClick={() => importHistory('adn')} className="min-h-[42px] rounded-lg border border-blue-200 bg-white px-3 text-sm font-semibold text-blue-700 disabled:opacity-50">
              {historyBusy ? 'Consultando...' : 'Importar histórico do ADN'}
            </button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input aria-label="Chave de NFS-e anterior" inputMode="numeric" maxLength={50}
              value={historyKey} onChange={event => setHistoryKey(event.target.value.replace(/\D/g, '').slice(0, 50))}
              placeholder="Chave de acesso (50 dígitos)" className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
            <button type="button" disabled={historyBusy || !integration?.consultationAvailable || integration?.environment !== 'production' || historyKey.length !== 50}
              onClick={() => importHistory('key')} className="min-h-[42px] rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white disabled:opacity-50">Importar por chave</button>
          </div>
          {historyFeedback && <p role="status" className="mt-2 text-sm text-emerald-800">{historyFeedback}</p>}
          {integration?.environment !== 'production' || !integration?.consultationAvailable
            ? <p className="mt-2 text-xs text-amber-800">Para consultar notas reais, configure a consulta oficial com certificado A1 no ambiente de produção.</p>
            : <p className="mt-2 text-xs text-slate-600">Consulta somente leitura de notas emitidas pelo CNPJ cadastrado, inclusive em outros emissores. O ADN pode disponibilizar apenas parte do histórico; repita para continuar. Nenhuma nota será reemitida.</p>}
        </div>
        {loading ? <p className="py-10 text-center text-sm text-slate-500">Carregando...</p> : filtered.length === 0 ? <p className="py-10 text-center text-sm text-slate-500">Nenhum documento encontrado.</p> :
          <>
          <div className="space-y-3 md:hidden">{filtered.map(note => (
            <article key={note.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" aria-label={`Documento ${note.id}, ${statusLabel(note.status)}`}>
              <div className="flex min-w-0 items-start justify-between gap-3">
                <div className="min-w-0"><p className="break-words text-sm font-semibold text-slate-900">{fiscalName(note.customerName)}</p><p className="mt-1 text-xs text-slate-500">{note.customerTaxId}</p></div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${note.status === 'AUTHORIZED' ? 'bg-emerald-50 text-emerald-800' : note.status === 'REJECTED' || note.status === 'CANCELED' ? 'bg-red-50 text-red-800' : 'bg-slate-100 text-slate-700'}`}>{statusLabel(note.status)}</span>
              </div>
              <p className="mt-2 text-xs text-slate-600">NFS-e: {note.invoiceNumber || '—'} · DPS: {note.dpsNumber || '—'} · Série: {note.dpsSeries || '—'}{note.origin === 'EXTERNAL' ? ' · Externa' : ''}</p>
              {note.lastError?.startsWith('CANCELAMENTO_PENDENTE') && <p className="mt-2 text-xs text-amber-800">Cancelamento pendente de confirmação oficial. Não reenvie.</p>}
              {note.status === 'REJECTED' && <details className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                <summary className="cursor-pointer text-xs font-semibold text-red-900">Por que a SEFIN rejeitou?</summary>
                <p className="mt-2 whitespace-pre-wrap break-words text-xs leading-relaxed text-red-900">{nfseRejectionReason(note)}</p>
                <p className="mt-2 text-xs text-red-800">Revise os dados indicados e use Reutilizar para preparar uma nova DPS. Não reenvie esta automaticamente.</p>
              </details>}
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
                <div><p className="text-xs text-slate-500">Competência</p><p className="mt-1 text-sm text-slate-700">{dateLabel(note.competenceDate)}</p></div>
                <div className="text-right"><p className="text-xs text-slate-500">Valor</p><p className="mt-1 text-base font-semibold text-slate-900">{money(note.serviceAmount)}</p></div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => reuseDocument(note)} className="min-h-[44px] flex-1 rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-700">Reutilizar</button>
                {note.status === 'DRAFT' && <button type="button" onClick={() => openEmission(note)} className="min-h-[44px] flex-1 rounded-xl border border-blue-200 px-3 text-sm font-semibold text-blue-700">Emitir nota</button>}
                {['AUTHORIZED', 'TEST_AUTHORIZED'].includes(note.status) && note.origin !== 'EXTERNAL' && note.accessKey && !note.lastError?.startsWith('CANCELAMENTO_PENDENTE') && <button type="button" disabled={cancelBusy || !integration?.cancellationAvailable} onClick={() => { setCancelNote(note); setCancelReason(''); setCancelCode('1'); }} className="min-h-[44px] rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-700 disabled:opacity-40">Cancelar NFS-e</button>}
                {['AUTHORIZED', 'TEST_AUTHORIZED', 'CANCELED'].includes(note.status) && note.accessKey && <button type="button" onClick={() => downloadPdf(note)} className="rounded-lg border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-700">Baixar PDF</button>}
                {['AUTHORIZED', 'TEST_AUTHORIZED', 'CANCELED'].includes(note.status) && note.accessKey && <button type="button" onClick={() => downloadXml(note)} className="min-h-[44px] flex-1 rounded-xl border border-emerald-200 px-3 text-sm font-semibold text-emerald-700">Baixar XML</button>}
                {note.status === 'PROCESSING' && <button type="button" disabled={emissionBusy}
                  onClick={() => reconcileEmission(note)}
                  className="min-h-[44px] flex-1 rounded-xl border border-amber-200 px-3 text-sm font-semibold text-amber-800">
                  Conciliar DPS na SEFIN
                </button>}
                {note.status === 'DRAFT' && <button type="button" onClick={() => editDraft(note)} className="min-h-[44px] flex-1 rounded-xl border border-blue-200 px-3 text-sm font-semibold text-blue-700">Editar</button>}
                {note.status === 'DRAFT' && <button type="button" onClick={() => removeDraft(note.id)} className="min-h-[44px] flex-1 rounded-xl border border-red-200 px-3 text-sm font-semibold text-red-700"><FaTrashAlt className="mr-1 inline" size={12}/> Excluir</button>}
              </div>
            </article>
          ))}</div>
          <div className="hidden overflow-x-auto md:block"><table className="w-full min-w-[650px] text-left text-sm">
            <thead className="border-b text-xs uppercase text-slate-500"><tr><th className="pb-3">Cliente</th><th className="pb-3">NFS-e / DPS / Série</th><th className="pb-3">Competência</th><th className="pb-3">Valor</th><th className="pb-3">Status</th><th className="pb-3 text-right">Ações</th></tr></thead>
            <tbody>{filtered.map(note =>
              <tr className="border-b last:border-0" key={note.id}>
                <td className="py-3"><div className="font-medium text-slate-900">{fiscalName(note.customerName)}</div><div className="text-xs text-slate-500">{note.customerTaxId}</div></td>
                <td className="py-3">NFS-e: {note.invoiceNumber || '—'}<br />DPS: {note.dpsNumber || '—'} · Série {note.dpsSeries || '—'}</td><td className="py-3">{dateLabel(note.competenceDate)}</td>
                <td className="py-3 font-medium">{money(note.serviceAmount)}</td>
                <td className="py-3">
                  {note.lastError?.startsWith('CANCELAMENTO_PENDENTE') && <p className="mb-2 text-xs text-amber-800">Cancelamento pendente de confirmação oficial.</p>}
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${note.status === 'REJECTED' ? 'bg-red-50 text-red-800' : 'bg-slate-100 text-slate-700'}`}>{statusLabel(note.status)}</span>
                  {note.status === 'REJECTED' && <details className="mt-2 max-w-sm rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-left">
                    <summary className="cursor-pointer text-xs font-semibold text-red-900">Ver motivo da rejeição</summary>
                    <p className="mt-2 whitespace-pre-wrap break-words text-xs leading-relaxed text-red-900">{nfseRejectionReason(note)}</p>
                    <p className="mt-1 text-xs text-red-800">Confira a causa antes de reutilizar.</p>
                  </details>}
                </td>
                <td className="py-3 text-right"><div className="inline-flex gap-2">
                  <button type="button" onClick={() => reuseDocument(note)} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">Reutilizar</button>
                  {note.status === 'DRAFT' && <><button type="button" onClick={() => openEmission(note)} className="rounded-lg border border-blue-100 px-3 py-2 text-xs font-semibold text-blue-700">Emitir nota</button><button type="button" onClick={() => editDraft(note)} className="rounded-lg border border-blue-100 px-3 py-2 text-xs font-semibold text-blue-700">Editar</button><button type="button" onClick={() => removeDraft(note.id)} className="rounded-lg p-2 text-xs text-red-600"><FaTrashAlt className="mr-1 inline"/> Excluir</button></>}
                  {['AUTHORIZED', 'TEST_AUTHORIZED'].includes(note.status) && note.origin !== 'EXTERNAL' && note.accessKey && !note.lastError?.startsWith('CANCELAMENTO_PENDENTE') && <button type="button" disabled={cancelBusy || !integration?.cancellationAvailable} onClick={() => { setCancelNote(note); setCancelReason(''); setCancelCode('1'); }} className="min-h-[44px] rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-700 disabled:opacity-40">Cancelar NFS-e</button>}
                {['AUTHORIZED', 'TEST_AUTHORIZED', 'CANCELED'].includes(note.status) && note.accessKey && <button type="button" onClick={() => downloadPdf(note)} className="rounded-lg border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-700">Baixar PDF</button>}
                {['AUTHORIZED', 'TEST_AUTHORIZED', 'CANCELED'].includes(note.status) && note.accessKey && <button type="button" onClick={() => downloadXml(note)} className="rounded-lg border border-emerald-200 px-3 py-2 text-xs font-semibold text-emerald-700">Baixar XML</button>}
                  {note.status === 'PROCESSING' && <button type="button" disabled={emissionBusy}
                    onClick={() => reconcileEmission(note)}
                    className="rounded-lg border border-amber-200 px-3 py-2 text-xs font-semibold text-amber-800">
                    Conciliar DPS
                  </button>}
                </div></td>
              </tr>)}</tbody>
          </table></div></>}
      </section>
      )}
    </div>
  );
}
